// PrimaryNav — the permission-gated domain links in the top bar.
//
// Renders the `primary` group of the ONE nav-config registry (config/nav.ts),
// gated through useCan + the pure visibility functions. Lives inside the
// Navbar's center-left zone (NOT a separate strip) — the reconcile decision
// that the top bar is single-sourced in AuthedLayout's Navbar.
//
// Hidden below md (the hamburger drawer carries the same links on phones).

import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { NavLink } from '../ui/NavLink.js';
import { HScroll } from '../ui/HScroll.js';
import { useCan } from '../../hooks/useCan.js';
import { useEntitlements } from '../../hooks/useEntitlements.js';
import { domainRoute, isOnDomain, navItemPath, visibleNavItems } from '../../config/nav.js';

export function PrimaryNav({ projectId }: { projectId: string }) {
  const { t } = useTranslation('nav');
  const can = useCan(projectId);
  const entitlements = useEntitlements(projectId);
  const ctx = { can, entitlements };
  const items = visibleNavItems('primary', ctx);

  // Text-only in the top bar (icons read as noise on a short horizontal nav +
  // widen the row); the registry's icons are kept for the vertical surfaces
  // (admin sidebar, mobile drawer). The nav is the flexible middle — it fills
  // the space between the logo and the pinned right cluster.
  //
  // HScroll owns the overflow affordance: on narrow desktop widths the strip
  // scrolls behind a hidden scrollbar with a both-edge fade signalling "← / →
  // more" (RTL-aware, self-hiding when it fits) — one implementation for the
  // nav / tab-bar / chip-row family.
  const { pathname } = useLocation();
  return (
    <div className="hidden md:block flex-1 min-w-0 ms-2">
      <HScroll className="h-14" fadeColor="var(--color-bg-page)">
        <nav aria-label={t('landmarks.primary')} className="flex h-14 items-stretch gap-1">
          {items.map((item) => (
            <NavLink
              key={item.id}
              to={navItemPath(projectId, domainRoute(item, ctx))}
              end={item.route === ''}
              // A domain's ground is its own route OR its area rail's (the Work area's pages are not under `/work`) —
              // the registry's one predicate, the phone drawer's too.
              active={isOnDomain(projectId, item, pathname)}
              variant="topbar"
            >
              <span className="whitespace-nowrap">{t(item.labelKey)}</span>
            </NavLink>
          ))}
        </nav>
      </HScroll>
    </div>
  );
}
